/** Section 5 — branding, reusing the Gift Generator in Vouchers.
 *
 *  NOTHING is rebuilt here: the named setups, the uploads, the brand library
 *  and the colour fields all read and write the SAME records Voucher Settings
 *  writes (`voucher_settings.gift_generator_setups` + the org's voucher
 *  colours), through the same normaliser and mirror. A change here shows up
 *  there and the other way round.
 */
import { useCallback, useEffect, useState } from 'react';
import { Check, Copy as CopyIcon, Loader2, Pencil, Plus, Save, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { btn } from './buttons';
import { getToken } from '../../../lib/session';
import { BrandAssetPicker } from '../../voucher_settings/BrandAssetPicker';
import { ImageField } from './ImageField';

const field = "w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm";

const GIFT_COPY = [
    ['headlineLine1', 'Headline line 1'],
    ['headlineHighlight', 'Headline highlight'],
    ['scratchInstruction', 'Scratch instruction'],
    ['revealTitle', 'Reveal title'],
    ['acceptButton', 'Accept button'],
    ['exchangeButton', 'Exchange button'],
];

const ColorInput = ({ label, value, onChange, testid, note }) => (
    <label className="block">
        <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
        <div className="flex items-center gap-2">
            <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)}
                   data-testid={testid} className="h-9 w-12 rounded border border-slate-200 dark:border-slate-700 bg-transparent" />
            <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
                   placeholder="(default)" className={`${field} font-mono`} />
            {value && (
                <button type="button" onClick={() => onChange('')}
                        data-testid={`${testid}-clear`}
                        className="text-xs text-slate-500 underline shrink-0">clear</button>
            )}
        </div>
        {/* What this colour actually changes on the fan's win screen. */}
        {note && (
            <span className="mt-1 block text-[11px] text-slate-500" data-testid={`${testid}-note`}>
                {note}
            </span>
        )}
    </label>
);

export const GoalBrandingBlock = ({ cfg, orgId, promotionId, onSave, onReload, onDraft }) => {
    const { t } = useTranslation();
    const [setups, setSetups] = useState([]);
    const [editId, setEditId] = useState(cfg.resolved_setup_id || null);
    const [gift, setGift] = useState(null);
    const [name, setName] = useState(cfg.organization_name || '');
    const [orgColors, setOrgColors] = useState({
        voucher_bg: '', voucher_text: '', voucher_accent: '',
    });
    const [orgLogo, setOrgLogo] = useState(null);
    const [picker, setPicker] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    const loadSetups = useCallback(async () => {
        const res = await api.get('/voucher-settings/gift-setups', { params: { organization_id: orgId } });
        setSetups(res.data.setups || []);
    }, [orgId]);

    const loadOrgSettings = useCallback(async () => {
        const res = await api.get('/voucher-settings', { params: { organization_id: orgId } });
        setOrgColors({
            voucher_bg: res.data.voucher_bg || '',
            voucher_text: res.data.voucher_text || '',
            voucher_accent: res.data.voucher_accent || '',
        });
        setOrgLogo(res.data.logo_url || null);
    }, [orgId]);

    const loadSetupConfig = useCallback(async (id) => {
        // No setup id, or a load that fails: show words, not a spinner.
        if (!id) { setGift(null); setLoadFailed(true); return; }
        setLoading(true);
        try {
            const res = await api.get(`/voucher-settings/gift-setups/${id}`, { params: { organization_id: orgId } });
            setGift(res.data.config || {});
            setLoadFailed(false);
        } catch (_e) {
            setGift(null);
            setLoadFailed(true);
        } finally { setLoading(false); }
    }, [orgId]);

    useEffect(() => { loadSetups(); loadOrgSettings(); }, [loadSetups, loadOrgSettings]);
    useEffect(() => { setName(cfg.organization_name || ''); }, [cfg.organization_name]);
    useEffect(() => { setEditId(cfg.resolved_setup_id || null); }, [cfg.resolved_setup_id]);
    useEffect(() => { loadSetupConfig(editId); }, [editId, loadSetupConfig]);
    // Logos, wallpaper and the setup's wording go straight to the phone
    // preview, before anything is saved.
    useEffect(() => { onDraft?.({ gift, orgColors, orgLogo }); }, [gift, orgColors, orgLogo, onDraft]);

    const g = (k, fallback = '') => (gift?.[k] ?? fallback);
    const setG = (k, v) => setGift((p) => ({ ...(p || {}), [k]: v }));
    const setGiftCopy = (lang, key, value) => setGift((p) => ({
        ...(p || {}),
        copy: { ...((p || {}).copy || {}), [lang]: { ...(((p || {}).copy || {})[lang] || {}), [key]: value } },
    }));

    const applySetup = async (value) => {
        await onSave(value === '__default__' ? { clear_gift_setup: true } : { gift_setup_id: value });
        toast.success('Tap’N Win setup updated');
    };

    const createSetup = async (copyFrom) => {
        const label = copyFrom ? 'Name for the duplicate' : 'Name for the new setup';
        const setupName = window.prompt(label);
        if (!setupName) return;
        setBusy(true);
        try {
            const res = await api.post('/voucher-settings/gift-setups',
                { organization_id: orgId, name: setupName, copy_from_setup_id: copyFrom || undefined });
            await loadSetups();
            setEditId(res.data.id);
            toast.success('Setup created');
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not create the setup');
        } finally { setBusy(false); }
    };

    const renameSetup = async () => {
        const current = setups.find((s) => s.id === editId);
        const next = window.prompt('New name', current?.name || '');
        if (!next) return;
        setBusy(true);
        try {
            await api.patch(`/voucher-settings/gift-setups/${editId}`, { organization_id: orgId, name: next });
            await loadSetups();
            toast.success('Setup renamed');
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not rename the setup');
        } finally { setBusy(false); }
    };

    const makeDefault = async () => {
        setBusy(true);
        try {
            await api.post(`/voucher-settings/gift-setups/${editId}/default`, null, { params: { organization_id: orgId } });
            await loadSetups();
            await onReload();
            toast.success('Set as the organization default');
        } catch (_e) { toast.error('Could not set the default'); }
        finally { setBusy(false); }
    };

    const applyOrgLogo = async (url, asset) => {
        try {
            const res = await api.post('/voucher-settings/logo-from-library',
                { asset_id: asset.id, organization_id: orgId });
            setOrgLogo(res.data.logo_url);
            await onReload();
        } catch (_e) { toast.error('Could not apply that logo'); }
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.post('/goal-game/admin/branding', {
                promotion_id: promotionId || null,
                organization_id: orgId,
                setup_id: editId,
                company_name: name,
                org_colors: orgColors,
                gift: gift ? {
                    merchant_logo_url: g('merchant_logo_url') || '',
                    logo_size_px: Number(g('logo_size_px', 96)) || 96,
                    wallpaper_url: g('wallpaper_url') || '',
                    scratch_surface_logo_url: g('scratch_surface_logo_url') || '',
                    scratch_surface_logo_opacity: Number(g('scratch_surface_logo_opacity', 30)) || 30,
                    scratch_surface_logo_size: Number(g('scratch_surface_logo_size', 38)) || 38,
                    voucher_design_mode: g('voucher_design_mode', 'existing') === 'custom' ? 'custom' : 'existing',
                    gg_voucher_bg: g('gg_voucher_bg') || '',
                    gg_voucher_text: g('gg_voucher_text') || '',
                    gg_voucher_accent: g('gg_voucher_accent') || '',
                    copy: gift.copy || {},
                } : undefined,
            });
            await onReload();
            await loadSetupConfig(editId);
            await loadOrgSettings();
            toast.success('Branding saved');
        } catch (_e) { toast.error('Could not save the branding'); }
        finally { setBusy(false); }
    };

    const selected = cfg.gift_setup_id || '__default__';

    return (
        <div className="space-y-5 pt-3">
            {/* which setup Tap’N Win runs */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                <p className="text-xs font-medium text-slate-500">Setup Tap’N Win runs</p>
                <select value={selected} onChange={(e) => applySetup(e.target.value)}
                        data-testid="gg-setup-select" className={field}>
                    <option value="__default__">Organization default</option>
                    {setups.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (default)' : ''}</option>
                    ))}
                </select>
                <p className="text-[11px] text-slate-400" data-testid="gg-setup-resolved">
                    Fan screens are using: <strong>{cfg.resolved_setup_name || '—'}</strong>
                </p>
            </div>

            {/* setup being edited + its actions */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3">
                <div className="flex flex-wrap items-end gap-2">
                    <label className="block flex-1 min-w-[200px]">
                        <span className="block text-xs font-medium text-slate-500 mb-1">Editing setup</span>
                        <select value={editId || ''} onChange={(e) => setEditId(e.target.value)}
                                data-testid="gg-setup-edit-select" className={field}>
                            {setups.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (default)' : ''}</option>
                            ))}
                        </select>
                    </label>
                    <button type="button" onClick={() => createSetup(null)} disabled={busy}
                            data-testid="gg-setup-create"
                            className={btn.smallSecondary}>
                        <Plus size={13} /> New
                    </button>
                    <button type="button" onClick={() => createSetup(editId)} disabled={busy || !editId}
                            data-testid="gg-setup-duplicate"
                            className={btn.smallSecondary}>
                        <CopyIcon size={13} /> Duplicate
                    </button>
                    <button type="button" onClick={renameSetup} disabled={busy || !editId}
                            data-testid="gg-setup-rename"
                            className={btn.smallSecondary}>
                        <Pencil size={13} /> Rename
                    </button>
                    <button type="button" onClick={makeDefault} disabled={busy || !editId}
                            data-testid="gg-setup-default"
                            className={btn.smallSecondary}>
                        <Star size={13} /> Set default
                    </button>
                </div>

                {loading && <Loader2 className="animate-spin text-slate-400" size={18} />}

                {!loading && !gift && loadFailed && (
                    <div className="space-y-3" data-testid="gg-branding-empty">
                        <p className="text-sm text-slate-500">
                            The branding for this organization could not be loaded — it may have no
                            gift setup yet. Creating one takes a second and starts from the Red,
                            White and Blue default.
                        </p>
                        <button type="button" data-testid="gg-branding-retry"
                                onClick={async () => { setLoadFailed(false); await onReload?.(); await loadSetups(); }}
                                className="rounded-lg bg-slate-900 dark:bg-white dark:text-slate-900 text-white px-3 py-1.5 text-sm font-semibold">
                            Create setup and try again
                        </button>
                    </div>
                )}

                {gift && (
                    <div className="space-y-4">
                        <ImageField
                            label="Merchant logo" hint="Top of the fan screen. This setup only."
                            value={g('merchant_logo_url')} endpoint="gift-merchant-logo"
                            responseKey="merchant_logo_url" orgId={orgId} setupId={editId}
                            libraryKind="merchant_logo" libraryName="Merchant logo"
                            onChange={(v) => setG('merchant_logo_url', v)}
                            onLibrary={() => setPicker({ kind: 'merchant_logo', apply: (url) => setG('merchant_logo_url', url) })}
                            testid="gg-merchant-logo" />
                        <label className="block max-w-xs">
                            <span className="block text-xs font-medium text-slate-500 mb-1">
                                Merchant logo size — {g('logo_size_px', 96)} px (20–240)
                            </span>
                            <input type="range" min={20} max={240} value={g('logo_size_px', 96)}
                                   onChange={(e) => setG('logo_size_px', Number(e.target.value))}
                                   data-testid="gg-merchant-logo-size" className="w-full" />
                        </label>

                        <ImageField
                            label="Wallpaper" hint="Fan screen background."
                            value={g('wallpaper_url')} endpoint="gift-wallpaper"
                            responseKey="wallpaper_url" orgId={orgId} setupId={editId}
                            libraryKind="wallpaper" libraryName="Wallpaper"
                            onChange={(v) => setG('wallpaper_url', v)}
                            onLibrary={() => setPicker({ kind: 'wallpaper', apply: (url) => setG('wallpaper_url', url) })}
                            testid="gg-wallpaper" />

                        <ImageField
                            label="Scratch surface logo" hint="The sponsor mark under the foil."
                            value={g('scratch_surface_logo_url')} endpoint="gift-scratch-logo"
                            responseKey="scratch_surface_logo_url" orgId={orgId} setupId={editId}
                            libraryKind="scratch_logo" libraryName="Scratch surface logo"
                            onChange={(v) => setG('scratch_surface_logo_url', v)}
                            onLibrary={() => setPicker({ kind: 'scratch_logo', apply: (url) => setG('scratch_surface_logo_url', url) })}
                            testid="gg-scratch-logo" />
                        <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">
                                    Scratch logo size — {g('scratch_surface_logo_size', 38)}% (25–100)
                                </span>
                                <input type="range" min={25} max={100} value={g('scratch_surface_logo_size', 38)}
                                       onChange={(e) => setG('scratch_surface_logo_size', Number(e.target.value))}
                                       data-testid="gg-scratch-logo-size" className="w-full" />
                            </label>
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">
                                    Scratch logo opacity — {g('scratch_surface_logo_opacity', 30)}% (15–60)
                                </span>
                                <input type="range" min={15} max={60} value={g('scratch_surface_logo_opacity', 30)}
                                       onChange={(e) => setG('scratch_surface_logo_opacity', Number(e.target.value))}
                                       data-testid="gg-scratch-logo-opacity" className="w-full" />
                            </label>
                        </div>

                        <div className="space-y-2">
                            <label className="block max-w-xs">
                                <span className="block text-xs font-medium text-slate-500 mb-1">Colours</span>
                                <select value={g('voucher_design_mode', 'existing')}
                                        onChange={(e) => setG('voucher_design_mode', e.target.value)}
                                        data-testid="gg-design-mode" className={field}>
                                    <option value="existing">Use the existing design</option>
                                    <option value="custom">Custom colours for this setup</option>
                                </select>
                            </label>
                            {g('voucher_design_mode', 'existing') === 'custom' && (
                                <div className="grid sm:grid-cols-3 gap-3">
                                    <ColorInput label="Background" value={g('gg_voucher_bg')} note={t('goalGame.colourBg')}
                                                onChange={(v) => setG('gg_voucher_bg', v)} testid="gg-color-bg" />
                                    <ColorInput label="Text" value={g('gg_voucher_text')} note={t('goalGame.colourText')}
                                                onChange={(v) => setG('gg_voucher_text', v)} testid="gg-color-text" />
                                    <ColorInput label="Accent" value={g('gg_voucher_accent')} note={t('goalGame.colourAccent')}
                                                onChange={(v) => setG('gg_voucher_accent', v)} testid="gg-color-accent" />
                                </div>
                            )}
                        </div>

                        <div>
                            <p className="text-xs font-medium text-slate-500 mb-2">
                                Gift Generator wording (shared with the voucher guest game)
                            </p>
                            <p className="text-[11px] text-slate-400 mb-2">
                                Tap’N Win fan screens read <strong>Scratch instruction</strong> and
                                <strong> Reveal title</strong>. The headline and the accept/exchange buttons
                                have no place on Tap’N Win screens — editing them here changes the
                                voucher guest game only.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {GIFT_COPY.map(([key, label]) => (['en', 'fr'].map((lang) => (
                                    <label key={`${key}-${lang}`} className="block">
                                        <span className="block text-xs text-slate-500 mb-1">
                                            {label} <span className="uppercase opacity-60">{lang}</span>
                                        </span>
                                        <input className={field}
                                               value={((gift.copy || {})[lang] || {})[key] || ''}
                                               onChange={(e) => setGiftCopy(lang, key, e.target.value)}
                                               placeholder="(default)"
                                               data-testid={`gg-giftcopy-${key}-${lang}`} />
                                    </label>
                                ))))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* organisation-level, shared with /admin/clients and /voucher-settings */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3">
                <p className="text-xs font-medium text-slate-500">Organization (shared with Clients and Voucher Settings)</p>
                <label className="block max-w-md">
                    <span className="block text-xs text-slate-500 mb-1">Company name</span>
                    <input className={field} value={name} onChange={(e) => setName(e.target.value)}
                           data-testid="gg-company-name" />
                </label>
                <ImageField
                    label="Organization logo" hint="Saved immediately. Used when a setup has no merchant logo."
                    value={orgLogo} endpoint="logo" fileField="logo" responseKey="logo_url"
                    orgId={orgId} libraryNote onChange={(v) => { if (!v) return; setOrgLogo(v); onReload(); }}
                    onLibrary={() => setPicker({ kind: 'logo', apply: applyOrgLogo, raw: true })}
                    testid="gg-org-logo" />
                <div className="grid sm:grid-cols-3 gap-3">
                    <ColorInput label="Background" value={orgColors.voucher_bg}
                                onChange={(v) => setOrgColors((p) => ({ ...p, voucher_bg: v }))} testid="gg-org-color-bg" />
                    <ColorInput label="Text" value={orgColors.voucher_text}
                                onChange={(v) => setOrgColors((p) => ({ ...p, voucher_text: v }))} testid="gg-org-color-text" />
                    <ColorInput label="Accent" value={orgColors.voucher_accent}
                                onChange={(v) => setOrgColors((p) => ({ ...p, voucher_accent: v }))} testid="gg-org-color-accent" />
                </div>
            </div>

            <button type="button" onClick={save} disabled={busy} data-testid="gg-branding-save"
                    className={btn.primary}>
                {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                Save branding
            </button>
            <p className="text-xs text-slate-500 flex items-start gap-1.5">
                <Check size={13} className="mt-0.5 shrink-0" />
                These are the same records Voucher Settings edits, so anything changed here appears
                there too. Gifts, inventory, the 2x exchange, lead generation and the sponsor
                redemption adapter are left exactly as they are.
            </p>

            <BrandAssetPicker
                open={!!picker}
                onOpenChange={(v) => { if (!v) setPicker(null); }}
                kind={picker?.kind}
                orgId={orgId}
                token={getToken()}
                isSuperAdmin
                onApply={(url, asset) => { picker?.apply?.(url, asset); setPicker(null); }}
            />
        </div>
    );
};

export default GoalBrandingBlock;
